import {environment} from '@/config/environment';
import {Capacitor} from '@capacitor/core';
import {appConfig} from '@/config/appConfig';

/**
 * Gets the authentication redirect URL for the current platform.
 *
 * For hybrid apps:
 * - MSAL/Azure: uses https base URL (universal/app link) so Azure SPA registration works
 * - OIDC: uses custom scheme {appId}://login
 *
 * @param path - Path ('msauth' for Azure/Microsoft, 'login' for OIDC)
 * @returns The redirect URL
 */
export function getAuthenticationRedirectUrl(path: 'msauth' | 'login'): string {
    if (!appConfig.isHybridApp) {
        // Use dev url for local development (*.local domains)
        if (appConfig.isLocalDevelopment) {
            return environment.VITE_DEV_BASE_URL!;
        }

        return window.location.origin;
    }

    // For MSAL on hybrid apps, use the https base URL so it can be registered
    // as a SPA redirect URI in Azure (custom schemes are no longer supported for SPA type)
    if (path === 'msauth') {
        return environment.VITE_BASE_URL!;
    }

    const platform = Capacitor.getPlatform();

    if (platform === 'ios') {
        const bundleId = environment.VITE_IOS_BUNDLE_ID;
        if (!bundleId) {
            throw new Error('VITE_IOS_BUNDLE_ID is not defined in environment variables');
        }
        return `${bundleId}://${path}`;
    }

    if (platform === 'android') {
        const packageId = environment.VITE_ANDROID_PACKAGE_ID;
        if (!packageId) {
            throw new Error('VITE_ANDROID_PACKAGE_ID is not defined in environment variables');
        }
        return `${packageId}://${path}`;
    }

    // Fallback for web or unknown platforms
    return window.location.origin;
}
